import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Fuel, Camera, Wallet, BarChart3, Shield, Users as UsersIcon, Truck, Package,
  ChevronRight, Sparkles, CheckCircle2, TrendingUp, Bell
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

/**
 * "How this works" — client-facing storyboard.
 * A single scrollable page that walks visitors through every capability in order.
 * Meant to be shown to prospects / new hires / auditors.
 */
export default function Tour() {
  const { user } = useAuth();
  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-16" data-testid="tour-root">
      <Hero />

      <SectionCard
        num="01"
        icon={Camera}
        title="Filler punches shift on the phone"
        subtitle="1 minute per shift · zero paperwork"
        bullets={[
          "Opens the app → taps 'Punch Shift'.",
          "Picks the nozzles they dispensed from. Big touch targets, no keyboard.",
          "For each nozzle: opening auto-fills from the previous shift's closing. Filler taps 📷 to scan the closing digits with the camera.",
          "Gemini 3 Vision reads the meter — filler just confirms with one tap.",
          "Bulk uploads all proof photos (meter close-ups, receipts) into one drop zone.",
          "Submits. Entry moves to the cashier as a DRAFT.",
        ]}
        cta={{ to: "/filler", label: "Open Filler Wizard" }}
        colorClass="border-emerald-500 bg-emerald-50/40"
      />

      <SectionCard
        num="02"
        icon={Wallet}
        title="Cashier closes the shift"
        subtitle="~3 minutes at shift handover"
        bullets={[
          "Opens the DRAFT that the filler handed over.",
          "All 9 payment modes (PhonePe, Paytm, Card, IOCL Pay, ICICI QR, Xtrapower Fleet, Extra Rewards, Miscellaneous, CMS Cash) are already listed — nothing to remember.",
          "Types the amount received for each mode. Rows with ₹0 auto-tag 'No transactions today' — no proof needed for zero.",
          "Rows with amount > 0 REQUIRE a screenshot proof (the merchant app's daily total). App blocks submit until all proofs are attached.",
          "Adds debtor sales — vehicle number and driver name captured for B2B invoicing.",
          "Enters cash-in-hand. Submit blocks if it doesn't match the derived cash within ±₹10.",
        ]}
        cta={{ to: "/shifts", label: "See DRAFT shifts" }}
        colorClass="border-blue-500 bg-blue-50/40"
      />

      <SectionCard
        num="03"
        icon={BarChart3}
        title="Supervisor reconciles against physical proofs"
        subtitle="Once per shift · catches every rupee"
        bullets={[
          "Opens Reconciliation → picks date + shift.",
          "The headline strip says it plainly: 'Gas Sales − Digital − Debtors − Adjustments = Expected Cash'.",
          "Next to it: the CMS Cash Deposit Slip amount (OCR-extracted from the uploaded photo).",
          "Variance turns red if the physical slip is short of expected. Status pill screams 'SHORT', 'OVER', 'SLIP_MISSING', or 'SLIP_UNREADABLE'.",
          "Any variance > ₹1 raises an AMOUNT_MISMATCH flag naming the exact cashier and shift.",
        ]}
        cta={{ to: "/reconciliation", label: "Open Reconciliation" }}
        colorClass="border-purple-500 bg-purple-50/40"
      />

      <SectionCard
        num="04"
        icon={Shield}
        title="Owner sees red flags pinpointed automatically"
        subtitle="Every morning · dashboard + email digest"
        bullets={[
          "Dashboard shows every headline number (fuel litres, digital ₹, credit ₹, flag exposure ₹) — every one is clickable.",
          "Click 'Total Fuels Sales ₹15,00,000' → see every underlying shift with cashier name and ₹. Click any row → open that exact shift + all its photos.",
          "Red Flags: repeat-offender table per cashier over 7/30/90 days sorted by rupee impact. Catches the ₹83,000 short-deposit patterns.",
          "Any unresolved flag > ₹5,000 older than 48h auto-emails the owner via Resend.",
        ]}
        cta={{ to: "/fraud", label: "Open Red Flags" }}
        colorClass="border-amber-500 bg-amber-50/40"
      />

      <SectionCard
        num="05"
        icon={Package}
        title="Lubricant inventory — buy, barcode, sell, restock"
        subtitle="Manager once/week"
        bullets={[
          "Enter a lubricant purchase: date, product, quantity, purchase price, selling price.",
          "System auto-generates one printable barcode per SKU. Print the sheet, stick on stock.",
          "Every sale: filler/cashier scans the barcode with the phone camera → SKU auto-populates → decrements stock.",
          "Live stock view flags low-stock (< 2 months of avg sales) items. Wastage requires a photo.",
        ]}
        cta={{ to: "/lubricants", label: "Open Lubricants" }}
        colorClass="border-teal-500 bg-teal-50/40"
      />

      <SectionCard
        num="06"
        icon={Truck}
        title="Fuel purchase — PO → Delivery → Invoice"
        subtitle="Manager places order, accountant closes it"
        bullets={[
          "Manager files a Purchase Order (fuel type, qty, expected date).",
          "Delivery arrives — filler records actual litres received against the PO.",
          "Accountant uploads the IOCL invoice. System auto-links PO ↔ Invoice.",
          "Discrepancy? Automatic flag: 'PO 5000L vs Delivered 4980L → 20L short from IOCL'.",
        ]}
        cta={{ to: "/fuel-purchase", label: "Open Fuel Purchase" }}
        colorClass="border-orange-500 bg-orange-50/40"
      />

      <SectionCard
        num="07"
        icon={UsersIcon}
        title="Team, attendance, credit ledger"
        subtitle="Monthly · 5 minutes total"
        bullets={[
          "Attendance: upload the monthly biometric export (Excel/CSV from your fingerprint machine). Done. System computes hours per person.",
          "Debtor ledger: per-customer credit sales, payments received, outstanding, ageing. Per-vehicle statements for B2B (Afcons/Bhoir/Pahal).",
          "Invites: pre-approve a new hire's email + role. They Google-sign-in once and land at the right access level.",
        ]}
        cta={{ to: "/attendance", label: "Open Attendance" }}
        colorClass="border-indigo-500 bg-indigo-50/40"
      />

      <SectionCard
        num="08"
        icon={Bell}
        title="Everything talks to your existing systems"
        subtitle="No lock-in · daily push/pull"
        bullets={[
          "Google Sheets bi-directional sync — your existing AppSheet spreadsheets keep getting updated automatically.",
          "Nightly detectors run: shortage tiers, sales drops, opening/closing gaps, deposit-late alerts.",
          "Resend email digest at your chosen hour with top-10 rupee-impact flags of the day.",
          "OCR pre-fills reconciliation values but never blocks manual entry — the app works even if every integration is off.",
        ]}
        cta={{ to: "/integrations", label: "Open Integrations" }}
        colorClass="border-slate-500 bg-slate-50/40"
      />

      <ClosingCTA role={user?.role} />
    </div>
  );
}

function Hero() {
  return (
    <div className="relative overflow-hidden rounded-sm border-2 border-slate-900 bg-slate-900 text-white p-6 sm:p-10">
      <div className="absolute -right-8 -top-8 opacity-10">
        <Fuel size={220} strokeWidth={1} />
      </div>
      <div className="relative">
        <div className="inline-flex items-center gap-1.5 bg-emerald-500/20 border border-emerald-400/30 rounded-sm px-2 py-1 text-[10px] uppercase tracking-widest text-emerald-300 font-bold">
          <Sparkles size={12} /> Full-stack POS for Velkar Auto Service
        </div>
        <h1 className="text-3xl sm:text-5xl font-black tracking-tight mt-3 leading-none">
          How this works<br /><span className="text-slate-400 font-normal text-2xl sm:text-3xl">in 8 clear steps</span>
        </h1>
        <p className="text-slate-300 mt-3 max-w-2xl leading-relaxed">
          A 3-minute read that shows the whole business from filler to owner —
          shift punching, cash reconciliation, red-flag alerts, inventory,
          purchases, and the team layer. Every step is live in the app;
          tap <em>&quot;Open …&quot;</em> at the end of each section to jump in.
        </p>
      </div>
    </div>
  );
}

function SectionCard({ num, icon: Icon, title, subtitle, bullets, cta, colorClass }) {
  return (
    <Card className={"border-2 rounded-sm p-5 " + colorClass} data-testid={`tour-section-${num}`}>
      <div className="flex items-start gap-4">
        <div className="shrink-0">
          <div className="text-xs font-black opacity-40 tracking-wider">{num}</div>
          <div className="bg-white rounded-sm p-2 border-2 border-current opacity-90 mt-1 inline-block">
            <Icon size={22} strokeWidth={2} />
          </div>
        </div>
        <div className="flex-1">
          <h3 className="text-xl sm:text-2xl font-bold text-slate-900">{title}</h3>
          <div className="text-xs font-bold uppercase tracking-wider text-slate-600 mt-0.5">{subtitle}</div>
          <ul className="mt-3 space-y-1.5">
            {bullets.map((b) => (
              <li key={b} className="flex items-start gap-2 text-sm text-slate-800 leading-relaxed">
                <CheckCircle2 size={14} className="shrink-0 mt-0.5 text-slate-700" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
          <div className="mt-4">
            <Link to={cta.to}>
              <Button variant="outline" size="sm" className="bg-white border-2 border-slate-900 hover:bg-slate-900 hover:text-white font-bold" data-testid={`tour-cta-${num}`}>
                {cta.label} <ChevronRight size={14} className="ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </Card>
  );
}

function ClosingCTA({ role }) {
  return (
    <Card className="border-2 border-slate-300 rounded-sm p-5 bg-white" data-testid="tour-close">
      <div className="flex items-center gap-3">
        <TrendingUp className="text-emerald-600" size={28} />
        <div className="flex-1">
          <h3 className="text-lg font-bold text-slate-900">That&apos;s the whole business, on one page.</h3>
          <p className="text-sm text-slate-600 mt-1">Every rupee dispensed → collected → deposited → reconciled → audited. No paperwork lost, no shift missed, no cashier untracked.</p>
        </div>
        <Link to="/">
          <Button className="bg-slate-900 hover:bg-slate-800 text-white font-bold">Back to Dashboard</Button>
        </Link>
      </div>
    </Card>
  );
}
